import { Line, Text } from "@react-three/drei";
import React from "react";
import { SERIF } from "../../../../constants";
import shallow from "zustand/shallow";
import calcRelativePaddings from "../../../../store/helpers/calcRelativePaddings";
import { DecorDimensionsProps } from "../types";
import convertDegreesToRadians from "../../../../utils/convertDegreesToRadians";
import { useStoreRoom } from "../../../../store";
import { getGroupSize } from "./createBboxMesh";

const DecorFloorDimensions: React.FC<DecorDimensionsProps> = (props) => {
    const { decor, bBoxDecor } = props;

    const bBoxDecorSize = bBoxDecor && getGroupSize(bBoxDecor).size;

    const room = useStoreRoom(
        (state) => ({
            roomLength: state.length,
            roomHeight: state.height,
            roomDepth: state.depth,
        }),
        shallow
    );

    const boundingZ = bBoxDecorSize?.y ? bBoxDecorSize.y : decor.height / 1000;
    const boundingY = bBoxDecorSize?.z ?? decor.depth;
    const boundingX = (bBoxDecorSize?.x ?? decor.width) * decor.center[0];

    const paddingsPayload = calcRelativePaddings(decor, room, bBoxDecor ? getGroupSize(bBoxDecor).size : undefined);

    const z = decor.position[1] - boundingZ / 2 + (decor.surfaceOffset ?? 0) / 1000; // 0.10

    const indent = 0.05;

    const xTopStart = boundingY * decor.center[2] + indent;
    const xTopEnd = paddingsPayload.top / 1000 + boundingY / 2 - indent;
    const xTopMiddle = boundingY / 2 + paddingsPayload.top / 2000;

    const yBottomStart = -boundingY * decor.center[2] - indent;
    const yBottomEnd = -(paddingsPayload.bottom / 1000 + boundingY / 2 - indent);
    const yBottomMiddle = -(boundingY / 2 + paddingsPayload.bottom / 2000);

    const xLeftStart = -boundingX - indent;
    const xLeftEnd = -(paddingsPayload.left / 1000 + boundingX - indent);
    const xLeftMiddle = -(boundingX + paddingsPayload.left / 2000);

    const xRightStart = boundingX + indent;
    const xRightEnd = paddingsPayload.right / 1000 + boundingX - indent;
    const xRightMiddle = boundingX + paddingsPayload.right / 2000;

    const rotation = 0; // decor.rotation // FIXME этот угол неправильно переворачивает размер

    return (
        <group>
            <group rotation={[0, rotation, 0]} position={[decor.position[0], boundingZ / 2, decor.position[2]]}>
                <Line
                    points={[
                        [-SERIF, z, xTopStart],
                        [SERIF, z, xTopStart],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [-SERIF, z, xTopEnd],
                        [SERIF, z, xTopEnd],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [0, z, xTopStart],
                        [0, z, xTopEnd],
                    ]}
                    lineWidth={0.5}
                    color="#EB3D00"
                />
                <Text
                    color="white"
                    anchorX="center"
                    anchorY="top"
                    position={[0.15, z, xTopMiddle]}
                    rotation={[convertDegreesToRadians(270), 0, 0]}
                >
                    {paddingsPayload.top}
                </Text>
            </group>
            <group rotation={[0, rotation, 0]} position={[decor.position[0], boundingZ / 2, decor.position[2]]}>
                <Line
                    points={[
                        [-SERIF, z, yBottomStart],
                        [SERIF, z, yBottomStart],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [-SERIF, z, yBottomEnd],
                        [SERIF, z, yBottomEnd],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [0, z, yBottomStart],
                        [0, z, yBottomEnd],
                    ]}
                    lineWidth={0.5}
                    color="#EB3D00"
                />
                <Text
                    color="white"
                    anchorX="center"
                    anchorY="middle"
                    position={[0.15, z, yBottomMiddle]}
                    rotation={[convertDegreesToRadians(270), 0, 0]}
                >
                    {paddingsPayload.bottom}
                </Text>
            </group>
            <group
                rotation={[convertDegreesToRadians(270), rotation, 0]}
                position={[decor.position[0], boundingZ / 2, decor.position[2]]}
            >
                <Line
                    points={[
                        [xLeftStart, -SERIF, z],
                        [xLeftStart, SERIF, z],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [xLeftEnd, -SERIF, z],
                        [xLeftEnd, SERIF, z],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [xLeftStart, 0, z],
                        [xLeftEnd, 0, z],
                    ]}
                    lineWidth={0.5}
                    color="#EB3D00"
                />
                <Text color="white" anchorX="center" anchorY="middle" position={[xLeftMiddle, 0.15, z]}>
                    {paddingsPayload.left}
                </Text>
            </group>
            <group
                rotation={[convertDegreesToRadians(270), rotation, 0]}
                position={[decor.position[0], boundingZ / 2, decor.position[2]]}
            >
                <Line
                    points={[
                        [xRightStart, -SERIF, z],
                        [xRightStart, SERIF, z],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [xRightEnd, -SERIF, z],
                        [xRightEnd, SERIF, z],
                    ]}
                    lineWidth={1}
                    color="#EB3D00"
                />
                <Line
                    points={[
                        [xRightStart, 0, z],
                        [xRightEnd, 0, z],
                    ]}
                    lineWidth={0.5}
                    color="#EB3D00"
                />
                <Text color="white" anchorX="center" anchorY="middle" position={[xRightMiddle, 0.15, z]}>
                    {paddingsPayload.right}
                </Text>
            </group>
        </group>
    );
};

export default DecorFloorDimensions;
